// internal-deps
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_ID, MARKETPLACE_CATEGORY, PAYMENT_CODES } from "@swiggy-private/payment-types";
import { Storage } from "@swiggy-private/core-ui";

// interfaces
import { Headers } from "@payment-client/interfaces/Http";
import {
    DashOrderContexts,
    DashPaymentTypes,
    PlaceOrderParams,
    ORDER_CONTEXT,
} from "@payment-client/interfaces/OrderPublic";
import {
    CreateTransactionParams,
    CreateTransactionRequestParams,
    GetPaymentOptionsParams,
    OrderContextParams,
    PaymentFallbackMethodParams,
    SuperContextParams,
    SwiggyOneContextParams,
} from "@payment-client/interfaces/PaymentApiPublic";
import { LinkableWallet } from "@payment-client/interfaces/WalletPublic";

// utils
import { getMarketplaceBusinessLine } from "@payment-client/utils/Headers";

// constants
import { DEEP_WALLET_SUFFIX } from "@payment-client/constants/WalletV3Endpoints";
import { PAYMENT_CONFIG } from "@payment-client/constants/Hosts";
import { WALLET_URLS } from "@payment-client/constants/WalletUrls";
import { SCENES_FULL_PAYMENT_ORDER_ID_KEY } from "@swiggy-private/checkout-core";
import { isScenesFullPaymentFlow } from "./Misc";

export * from "./publicOrderExtensions";

export const MERCHANT_ID = "Swiggy";

export const getEditOrderExtraParams = (headers: Headers): OrderContextParams => ({
    context: ORDER_CONTEXT.EDIT,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_key: (headers["cartkey"] as string) || "",
});

export const getPostOrderTipContextParams = (headers: Headers): OrderContextParams => ({
    context: ORDER_CONTEXT.POST_ORDER_TIPPING,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_key: (headers["cartkey"] as string) || "",
});

export const getPayOnDeliveryPaymentOptionsParams = (headers: Headers): GetPaymentOptionsParams => ({
    context: ORDER_CONTEXT.PAY_ON_DELIVERY,
    paymentLinkId: (headers["cartkey"] as string) || "",
});

export const getPayOnDeliveryContextParams = (headers: Headers): OrderContextParams => ({
    context: ORDER_CONTEXT.PAY_ON_DELIVERY,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_key: (headers["cartkey"] as string) || "",
});

export const getPaymentOptionsParams = (headers: Headers): GetPaymentOptionsParams => ({
    paymentLinkId: (headers["cartkey"] as string) || "",
});

// This same object format is also used for Handpicked, Dineout, Instamart, Instamax businessline, any change heres pls confirm if it would be needed in other business line as well as well or not (check Order.ts file)
const getPlaceOrderParams = (headers: Headers, data: PlaceOrderParams, orderType?: string): Record<string, unknown> => {
    const markerPlaceCategory = orderType || String(headers["marketplacecategory"]);
    const paymentType = DashPaymentTypes.PRE_PAYMENT;
    /* eslint-disable @typescript-eslint/naming-convention */
    const paymentMethodMeta = data.payment_method_meta ?? {};
    const inAppUpiParams = data.in_app_upi_params ?? {};

    // for EMI transaction, payment method we will get from place order will be juspay but we need to pass Third-Party-EMI
    const paymentMethod = data.isEmi === true ? PAYMENT_CODES.THIRD_PARTY_EMI : data.payment_cod_method;

    const emiInfo =
        data.isEmi === true
            ? {
                  tenure: data.emiTenure,
                  bank_code: data.bankCode,
                  emi_type: data.emiType,
                  emi_bank: data.emiBank,
                  txn_emi_type: data.txnEmiType,
              }
            : {};

    return {
        ...data,
        payment_type: paymentType,
        payment_info: {
            payment_method: paymentMethod,
            order_context: DashOrderContexts.ORDER_JOB,
            payment_type: paymentType,
            emi_info: { ...emiInfo },
            metadata: JSON.stringify({
                ...data,
                order_meta: {
                    ordered_from: markerPlaceCategory,
                    transaction_type: paymentType,
                },
                payment_method_meta: {
                    ...paymentMethodMeta,
                    swiggy_pay_amount: data.swiggy_pay_amount,
                    token: data.token,
                    provider_category: data.provider_category,
                    provider: data.provider,
                },
                force_validate_coupon: data.force_validate_coupon,
                ...inAppUpiParams,
            }),
            transaction_amount: data.transaction_amount,
        },
        cartType: markerPlaceCategory.toUpperCase(),
    };
    /* eslint-enable @typescript-eslint/naming-convention  */
};

// This same object format is also used for Dineout businessline, any change heres pls confirm if it would be needed in dineout as well or not (check Order.ts file)
export const getDashPlaceOrderParams = (headers: Headers, data: PlaceOrderParams): Record<string, unknown> => {
    return getPlaceOrderParams(headers, data);
};

export const getAssurePlaceOrderParams = (headers: Headers, data: PlaceOrderParams): Record<string, unknown> => {
    return getPlaceOrderParams(headers, data);
};

export const getHandpickedPlaceOrderParams = (headers: Headers, data: PlaceOrderParams): Record<string, unknown> => {
    return getPlaceOrderParams(headers, data, MARKETPLACE_CATEGORY.HANDPICKED);
};

export const getSwiggySuperParams = (headers: Headers): SuperContextParams => ({
    context: ORDER_CONTEXT.SWIGGY_SUPER,
    planId: Number(headers["planid"]) || 0,
    quantity: Number(headers["quantity"]) || 0,
});

export const getSwiggyOneParams = (): SwiggyOneContextParams => ({
    context: ORDER_CONTEXT.SWIGGY_ONE,
});

export const getRegularHeaders = (headers: Headers): Headers => {
    if (PAYMENT_CONFIG.USE_NEW_PAYMENT_ENDPOINT === "true") {
        headers["m_id"] = headers["m_id"] ?? MARKETPLACE_ID.CHECKOUT;
    }

    return headers;
};

/* eslint-disable @typescript-eslint/naming-convention */
export const getPaymentFallbackMethodsParamsByBusinessLine = (
    marketplaceBusinessLine: string,
    orderId: string,
    cartKey: string,
): PaymentFallbackMethodParams => {
    switch (marketplaceBusinessLine) {
        case MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY:
            // For Pay on Delivery, we need to pass the transaction_id to get the payment fallback methods
            // From source of API call we are sending the transaction id in value of order_id
            return {
                context: ORDER_CONTEXT.PAY_ON_DELIVERY,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                order_id: orderId,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                cart_key: cartKey,
            };
        default:
            return {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                order_id: orderId,
            };
    }
};

export const prepareCreateTxnParams = (params: CreateTransactionRequestParams): CreateTransactionParams => {
    const txnParams: CreateTransactionParams = {
        amount: params.amount,
        transaction_type: params.transactionType,
        card_bin_number: params.cardBinNumber,
        payment_method: params.paymentMethod,
        payment_method_meta: {},
    };
    txnParams.payment_method_meta = {
        juspay_redirect_url: params.paymentReturnUrl || WALLET_URLS.TXN_RETURN_URL,
    };

    return txnParams;
};
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * This method is added for handling legacy support for
 * wallet payment code in case of paytm, mobikwik, freecharge
 * Right now for web get payment request, we get `paytm` as code
 * but accepts `paytm-sso`, this handles that legacy code
 * Need to remove this handle, when 100% migration in done on web payments
 * @param paymentMethod string
 */
export function getPaymentCode(paymentMethod: string): string {
    switch (paymentMethod.toLowerCase()) {
        // case LinkableWallet.FREECHARGE:
        case LinkableWallet.PAYTM:
        case LinkableWallet.MOBIKWIK:
            return paymentMethod + DEEP_WALLET_SUFFIX;
        default:
            return paymentMethod;
    }
}

export const getDineoutPlaceOrderParams = (headers: Headers, data: PlaceOrderParams): Record<string, unknown> => {
    const markerPlaceBusinessLine = getMarketplaceBusinessLine(headers);

    const _isScenesFullPaymentFlow = isScenesFullPaymentFlow();

    const paymentType = DashPaymentTypes.PRE_PAYMENT;

    // Determine order payment type based on flow for the scenes full payment flow we need SCENES_FULL_PAYMENT as a payment type for the other flows we need PRE_PAYMENT
    const orderPaymentType = _isScenesFullPaymentFlow ? DashPaymentTypes.SCENES_FULL_PAYMENT : paymentType;

    /* eslint-disable @typescript-eslint/naming-convention */
    const paymentMethodMeta = data.payment_method_meta ?? {};
    const inAppUpiParams = data.in_app_upi_params ?? {};

    const scenesFullPaymentOrderId = _isScenesFullPaymentFlow
        ? Storage.getItem<string>(SCENES_FULL_PAYMENT_ORDER_ID_KEY)
        : undefined;

    return {
        ...data,
        payment_type: orderPaymentType,
        order_id: scenesFullPaymentOrderId, // for the scenes full payment flow, we need to pass the order id to the order API
        payment_info: {
            payment_method: data.payment_cod_method,
            order_context: DashOrderContexts.ORDER_JOB,
            payment_type: paymentType,
            metadata: JSON.stringify({
                ...data,
                order_meta: {
                    ordered_from: markerPlaceBusinessLine,
                    transaction_type: paymentType,
                },
                payment_method_meta: {
                    ...paymentMethodMeta,
                    swiggy_pay_amount: data.swiggy_pay_amount,
                    token: data.token,
                    provider_category: data.provider_category,
                    provider: data.provider,
                },
                force_validate_coupon: data.force_validate_coupon,
                ...inAppUpiParams,
            }),
            transaction_amount: data.transaction_amount,
        },
        cartType: markerPlaceBusinessLine.toUpperCase(),
        merchant_id: MERCHANT_ID, // default is swiggy on backend
    };
};

